/** Pure Reseller profiles display mapping — no I/O. D5B. */

import type { StatusTone } from "./overview-ops";
import type { ResellerProfileSummary } from "./api";

export type ResellerSortKey = "name" | "code" | "state" | "updated" | "sites";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export const RESELLER_PAGE_LABEL = "پروفایل‌های نماینده";

export const RESELLER_PAGE_NOTE =
  "پروفایل‌های نماینده (Reseller) برندینگ مشترک چند سایت را مدیریت می‌کند. اولویت برندینگ و خروجی عمومی پورتال تغییر نمی‌کند؛ بدون تصویر خارجی یا تغییر ZIP/روتر.";

export const RESELLER_STATE_LABELS: Record<string, string> = {
  draft: "پیش‌نویس",
  published: "منتشرشده",
  archived: "بایگانی",
};

export function resellerStateDisplay(state: string): StatusDisplay {
  const label = RESELLER_STATE_LABELS[state] ?? state;
  if (state === "published") {
    return { label, tone: "success", detail: "برندینگ از API عمومی برای سایت‌های متصل قابل resolve است" };
  }
  if (state === "draft") {
    return { label, tone: "info", detail: "هنوز منتشر نشده — خروجی عمومی اعمال نشده" };
  }
  if (state === "archived") {
    return { label, tone: "neutral", detail: "از خروجی عمومی حذف شده؛ اتصال سایت‌ها ممکن است حفظ باشد" };
  }
  return { label, tone: "neutral" };
}

/**
 * Logo / branding availability — not MIME validation (that stays on upload API).
 * Distinguishes assigned public logo, staging-only logo, and local/fallback absence.
 */
export function resellerBrandingDisplay(row: ResellerProfileSummary): StatusDisplay {
  if (row.logoPath) {
    return {
      label: "لوگوی اختصاصی",
      tone: "success",
      detail: "برندینگ نماینده اختصاص داده شده (مسیر عمومی موجود)",
    };
  }
  if (row.hasStagingLogo) {
    return {
      label: "لوگوی موقت",
      tone: "warning",
      detail: "لوگو در فضای موقت است؛ پس از انتشار عمومی می‌شود — تا آن زمان fallback محلی",
    };
  }
  return {
    label: "بدون لوگو / fallback",
    tone: "neutral",
    detail: "لوگوی نماینده نیست — برندینگ محلی/پیش‌فرض پورتال",
  };
}

/** Same-origin authenticated preview. Admin host does not serve /uploads/*. */
export function resellerAdminLogoPreviewSrc(input: {
  profileId: string;
  logoPath: string | null;
  hasStagingLogo?: boolean;
  version?: number;
}): string | null {
  if (!input.logoPath && !input.hasStagingLogo) return null;
  const version = input.version ?? 0;
  return `/api/admin/reseller-profiles/${encodeURIComponent(input.profileId)}/logo-preview?v=${version}`;
}

const LOGO_MIME_OK = new Set(["image/jpeg", "image/png", "image/webp"]);

/** Client precheck before POST /logo. Returns a catalog key or null if OK. */
export function resellerLogoClientValidationError(
  file: File,
  limits: { maxBytes: number; allowedMime: readonly string[] },
): "logoErrTooLarge" | "logoErrInvalidType" | "logoErrNoFile" | null {
  if (!file || file.size <= 0) return "logoErrNoFile";
  if (file.size > limits.maxBytes) return "logoErrTooLarge";
  if (file.type && !limits.allowedMime.includes(file.type) && !LOGO_MIME_OK.has(file.type)) {
    return "logoErrInvalidType";
  }
  if (!file.type && !/\.(jpe?g|png|webp)$/i.test(file.name)) {
    return "logoErrInvalidType";
  }
  return null;
}

/** Map English API logo errors to resellerUi catalog keys. */
export function mapResellerLogoApiError(
  message: string,
):
  | "logoErrTooLarge"
  | "logoErrInvalidType"
  | "logoErrInvalidFormat"
  | "logoErrDimensions"
  | "logoErrNoFile"
  | "logoErrUnableDimensions"
  | null {
  const m = message.trim();
  if (m === "File too large") return "logoErrTooLarge";
  if (m === "Invalid content type") return "logoErrInvalidType";
  if (m.startsWith("Invalid image format")) return "logoErrInvalidFormat";
  if (m.startsWith("Image dimensions must be between")) return "logoErrDimensions";
  if (m === "No file uploaded") return "logoErrNoFile";
  if (m === "Unable to read image dimensions") return "logoErrUnableDimensions";
  return null;
}

export function resellerAssignmentDisplay(row: ResellerProfileSummary): StatusDisplay {
  const n = row.linkedSiteIds.length;
  if (n === 0) {
    return {
      label: "بدون اتصال سایت",
      tone: "warning",
      detail: "هنوز به سایتی وصل نشده",
    };
  }
  return {
    label: `${n} سایت متصل`,
    tone: "info",
    detail: "اتصال سایت‌ها بدون ZIP یا آپلود روتر",
  };
}

export function matchesResellerClientFilter(
  row: ResellerProfileSummary,
  filter: string,
): boolean {
  if (filter === "all") return true;
  if (filter === "has_logo") return Boolean(row.logoPath);
  if (filter === "staging_logo") return row.hasStagingLogo && !row.logoPath;
  if (filter === "fallback") return !row.logoPath && !row.hasStagingLogo;
  if (filter === "assigned") return row.linkedSiteIds.length > 0;
  if (filter === "unassigned") return row.linkedSiteIds.length === 0;
  return true;
}

export function filterAndSortResellerProfiles(
  rows: ResellerProfileSummary[],
  opts: { clientFilter: string; sort: ResellerSortKey },
): ResellerProfileSummary[] {
  const filtered = rows.filter((r) => matchesResellerClientFilter(r, opts.clientFilter));
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "code") return a.code.localeCompare(b.code, "en");
    if (opts.sort === "state") return a.state.localeCompare(b.state);
    if (opts.sort === "updated") return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
    if (opts.sort === "sites") return b.linkedSiteIds.length - a.linkedSiteIds.length;
    return a.operationalName.localeCompare(b.operationalName, "fa");
  });
  return sorted;
}

export type RelatedResellerLink = { id: string; label: string; to: string };

export function relatedResellerLinks(): RelatedResellerLink[] {
  return [
    { id: "sites", label: "سایت‌ها و گروه‌ها", to: "/sites" },
    { id: "versions", label: "تاریخچه انتشار", to: "/versions" },
    { id: "overview", label: "نمای کلی", to: "/" },
  ];
}
